import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Trash2,
  Save,
  X,
  Package,
  Tag,
  Gift,
  Percent,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Edit2,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { compositeProductService } from '@/services/compositeProductService';
import { productService } from '@/services/productService';
import { Product } from '@/types/product';
import {
  ProductAggregationMaster,
  ProductAggregationDetail,
  ProductAggregationDetailUpdateData,
  calcDetailEffectivePrice,
  calcCompositeTotal,
} from '@/types/compositeProduct';
import { clsx } from 'clsx';

// ─── Tipos internos ───────────────────────────────────────────────────────────

interface MasterRow extends ProductAggregationMaster {
  isExpanded: boolean;
  details: DetailRow[];
  detailsLoaded: boolean;
  isEditingDescription: boolean;
  tempDescription: string;
  savingDescription: boolean;
  loadingDetails: boolean;
}

interface DetailRow extends ProductAggregationDetail {
  isEditing: boolean;
  tempQty: string;
  tempPromoPrice: string;
  isSaving: boolean;
  hasError: boolean;
  errorMessage?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function toDetailRow(d: ProductAggregationDetail): DetailRow {
  return {
    ...d,
    isEditing: false,
    tempQty: String(d.skuDetailQty),
    tempPromoPrice: d.promoPrice != null ? String(d.promoPrice) : '',
    isSaving: false,
    hasError: false,
  };
}

// ─── Modal de busca de produtos ───────────────────────────────────────────────

interface ProductSearchModalProps {
  onSelect: (product: Product) => void;
  onClose: () => void;
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({ onSelect, onClose }) => {
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const pageSize = 10;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const doSearch = useCallback(async (q: string, p: number) => {
    setLoading(true);
    try {
      const result = await productService.searchProducts(q || '', p, pageSize, { active: true });
      setProducts(result.products ?? []);
      const pagination = result.pagination as any;
      setTotalPages(pagination?.total_pages ?? pagination?.totalPages ?? 1);
      setTotalItems(pagination?.total ?? pagination?.total_items ?? result.products?.length ?? 0);
    } catch {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce na busca
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      doSearch(query, 1);
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Quando muda de página
  useEffect(() => {
    doSearch(query, page);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSelect = (product: Product) => {
    onSelect(product);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Package size={18} className="text-emerald-600" />
            <h2 className="text-base font-semibold text-slate-800">Buscar Produto</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search input */}
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            {loading && (
              <RefreshCw size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar por SKU ou descrição..."
              className="w-full pl-9 pr-8 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {products.length === 0 && !loading ? (
            <div className="py-10 text-center text-slate-400">
              <Package size={36} className="mx-auto mb-2 text-slate-300" />
              <p className="text-sm">Nenhum produto encontrado</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Descrição</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">Preço</th>
                  <th className="px-4 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">Estoque</th>
                </tr>
              </thead>
              <tbody>
                {products.map(product => (
                  <tr
                    key={product.id}
                    onClick={() => handleSelect(product)}
                    className="border-b border-slate-100 cursor-pointer hover:bg-emerald-50 transition-colors"
                  >
                    <td className="px-4 py-2.5 font-mono font-semibold text-slate-800 text-xs">
                      {product.sku}
                    </td>
                    <td className="px-4 py-2.5 text-slate-700 max-w-xs">
                      <span className="truncate block" title={product.description}>{product.description}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-700 whitespace-nowrap">
                      {product.price != null ? formatCurrency(product.price) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-center text-slate-500">
                      {product.stock_quantity_available ?? product.stock_quantity ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 text-xs text-slate-500">
          <span>{totalItems} produto(s) encontrado(s)</span>
          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page === 1 || loading}
                onClick={() => setPage(p => p - 1)}
                className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
              >
                ← Anterior
              </button>
              <span>Página {page} de {totalPages}</span>
              <button
                type="button"
                disabled={page === totalPages || loading}
                onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-40"
              >
                Próxima →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Sub-componente: linha de detalhe ─────────────────────────────────────────

interface DetailRowProps {
  row: DetailRow;
  canWrite: boolean;
  onUpdate: (fields: Partial<DetailRow>) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

const DetailRowItem: React.FC<DetailRowProps> = ({
  row,
  canWrite,
  onUpdate,
  onSave,
  onCancel,
  onDelete,
}) => {
  const effectivePrice = calcDetailEffectivePrice(row);
  const totalLine = row.skuDetailQty * effectivePrice;
  const editing = row.isEditing && canWrite;

  return (
    <tr className="bg-slate-50 border-b border-slate-100 text-sm">
      {/* Coluna de indentação – alinha com o toggle da master row */}
      <td className="w-10" />

      {/* SKU Detalhe */}
      <td className="px-4 py-2 font-mono text-xs text-slate-700 whitespace-nowrap">
        {row.skuDetail}
      </td>

      {/* Descrição do produto filho */}
      <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={row.productDescription}>
        {row.productDescription ?? '—'}
      </td>

      {/* Preço original */}
      <td className="px-4 py-2 text-slate-500 text-right whitespace-nowrap">
        {row.originalPrice != null ? formatCurrency(row.originalPrice) : '—'}
      </td>

      {/* Qty – editável quando isEditing */}
      <td className="px-4 py-2 text-center">
        {editing ? (
          <input
            type="number"
            min="1"
            value={row.tempQty}
            onChange={e => onUpdate({ tempQty: e.target.value })}
            className="w-16 text-center border border-slate-300 rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
            autoFocus
          />
        ) : (
          <span className="font-medium text-slate-700">{row.skuDetailQty}</span>
        )}
      </td>

      {/* IsGift – toggle sempre clicável quando canWrite */}
      <td className="px-4 py-2 text-center">
        {canWrite ? (
          <button
            type="button"
            onClick={() => onUpdate({ isGift: !row.isGift, isEditing: true })}
            className={clsx(
              'inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-medium transition-colors',
              row.isGift
                ? 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
            )}
          >
            <Gift size={12} />
            {row.isGift ? 'Sim' : 'Não'}
          </button>
        ) : (
          <span className={clsx(
            'inline-flex text-xs rounded-full px-2 py-0.5 font-medium',
            row.isGift ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500',
          )}>
            {row.isGift ? 'Sim' : 'Não'}
          </span>
        )}
      </td>

      {/* UsePromoPrice – toggle sempre clicável quando canWrite */}
      <td className="px-4 py-2 text-center">
        {canWrite ? (
          <button
            type="button"
            onClick={() => onUpdate({ usePromoPrice: !row.usePromoPrice, isEditing: true })}
            className={clsx(
              'inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 font-medium transition-colors',
              row.usePromoPrice
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200',
            )}
          >
            <Percent size={12} />
            {row.usePromoPrice ? 'Sim' : 'Não'}
          </button>
        ) : (
          <span className={clsx(
            'inline-flex text-xs rounded-full px-2 py-0.5 font-medium',
            row.usePromoPrice ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500',
          )}>
            {row.usePromoPrice ? 'Sim' : 'Não'}
          </span>
        )}
      </td>

      {/* PromoPrice – campo de input aparece sempre que isEditing, independente de usePromoPrice */}
      <td className="px-4 py-2 text-right">
        {editing ? (
          <input
            type="number"
            min="0"
            step="0.0001"
            value={row.tempPromoPrice}
            onChange={e => onUpdate({ tempPromoPrice: e.target.value })}
            disabled={!row.usePromoPrice}
            className={clsx(
              'w-24 text-right border rounded px-1 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500',
              row.usePromoPrice
                ? 'border-slate-300'
                : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed',
            )}
            placeholder={row.usePromoPrice ? '0,00' : '—'}
            onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
          />
        ) : row.promoPrice != null ? (
          <span className="text-amber-600 text-xs font-medium">
            {formatCurrency(row.promoPrice)}
          </span>
        ) : (
          <span className="text-slate-400 text-xs">—</span>
        )}
      </td>

      {/* Preço efetivo */}
      <td className="px-4 py-2 text-right font-medium text-emerald-700 whitespace-nowrap text-xs">
        {row.isGift ? (
          <span className="text-purple-600">Grátis</span>
        ) : (
          formatCurrency(effectivePrice)
        )}
      </td>

      {/* Total linha */}
      <td className="px-4 py-2 text-right font-semibold text-slate-700 whitespace-nowrap text-xs">
        {formatCurrency(totalLine)}
      </td>

      {/* Ações */}
      <td className="px-4 py-2 whitespace-nowrap">
        <div className="flex items-center justify-end gap-1">
          {editing ? (
            <>
              <button
                type="button"
                onClick={onSave}
                disabled={row.isSaving}
                className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                title="Salvar"
              >
                {row.isSaving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
              </button>
              <button
                type="button"
                onClick={onCancel}
                disabled={row.isSaving}
                className="p-1 text-slate-400 hover:text-slate-600 disabled:opacity-50"
                title="Cancelar"
              >
                <X size={14} />
              </button>
            </>
          ) : (
            canWrite && (
              <button
                type="button"
                onClick={() => onUpdate({ isEditing: true })}
                className="p-1 text-slate-400 hover:text-emerald-600"
                title="Editar linha"
              >
                <Edit2 size={14} />
              </button>
            )
          )}
          <PermissionGuard permission="composite_products.delete">
            <button
              type="button"
              onClick={onDelete}
              className="p-1 text-slate-400 hover:text-red-500"
              title="Remover produto filho"
            >
              <Trash2 size={14} />
            </button>
          </PermissionGuard>
        </div>
        {row.hasError && row.errorMessage && (
          <p className="text-red-500 text-xs mt-1 text-right">{row.errorMessage}</p>
        )}
      </td>
    </tr>
  );
};

// ─── Sub-componente: formulário de novo detalhe ───────────────────────────────

interface AddDetailFormProps {
  skuMaster: string;
  onAdd: (
    skuDetail: string,
    qty: number,
    isGift: boolean,
    promoPrice: number | null,
    usePromoPrice: boolean,
  ) => void;
  onCancel: () => void;
  saving: boolean;
  error?: string;
}

const AddDetailForm: React.FC<AddDetailFormProps> = ({
  onAdd,
  onCancel,
  saving,
  error,
}) => {
  const [skuDetail, setSkuDetail] = useState('');
  const [selectedProductDesc, setSelectedProductDesc] = useState<string | null>(null);
  const [qty, setQty] = useState('1');
  const [isGift, setIsGift] = useState(false);
  const [usePromoPrice, setUsePromoPrice] = useState(false);
  const [promoPrice, setPromoPrice] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const skuInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    skuInputRef.current?.focus();
  }, []);

  const handleProductSelect = (product: Product) => {
    setSkuDetail(product.sku);
    setSelectedProductDesc(product.description);
    setLocalError(undefined);
  };

  const handleSubmit = () => {
    setLocalError(undefined);
    if (!skuDetail.trim()) {
      setLocalError('Informe ou selecione o SKU do produto filho');
      return;
    }
    const qtyN = parseInt(qty, 10);
    if (isNaN(qtyN) || qtyN < 1) {
      setLocalError('Quantidade deve ser maior que zero');
      return;
    }
    const promoPriceN =
      usePromoPrice && promoPrice.trim() ? parseFloat(promoPrice) : null;
    if (usePromoPrice && (promoPriceN === null || isNaN(promoPriceN) || promoPriceN < 0)) {
      setLocalError('Preço promocional inválido');
      return;
    }
    onAdd(skuDetail.trim().toUpperCase(), qtyN, isGift, promoPriceN, usePromoPrice);
  };

  const displayError = localError ?? error;

  return (
    <>
      {showModal && (
        <ProductSearchModal
          onSelect={handleProductSelect}
          onClose={() => setShowModal(false)}
        />
      )}
      <tr className="bg-emerald-50 border-b border-emerald-100">
        <td colSpan={11} className="px-4 py-4">
          <div className="space-y-3">
            {/* Linha 1: SKU + Qty */}
            <div className="flex flex-wrap items-end gap-4">
              {/* SKU com botão de busca */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-600">SKU Produto Filho *</label>
                <div className="flex items-center gap-1">
                  <input
                    ref={skuInputRef}
                    type="text"
                    value={skuDetail}
                    onChange={e => {
                      setSkuDetail(e.target.value.toUpperCase());
                      setSelectedProductDesc(null);
                    }}
                    placeholder="SKU"
                    maxLength={10}
                    className="w-32 border border-slate-300 rounded-l px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 font-mono uppercase"
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSubmit();
                      if (e.key === 'Escape') onCancel();
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    title="Buscar produto"
                    className="border border-l-0 border-slate-300 rounded-r px-2 py-1.5 bg-white hover:bg-slate-50 text-slate-500 hover:text-emerald-600 transition-colors"
                  >
                    <Search size={14} />
                  </button>
                </div>
                {selectedProductDesc && (
                  <span className="text-xs text-emerald-700 truncate max-w-[180px]" title={selectedProductDesc}>
                    {selectedProductDesc}
                  </span>
                )}
              </div>

              {/* Qty */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-600">Quantidade *</label>
                <input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={e => setQty(e.target.value)}
                  className="w-20 border border-slate-300 rounded px-2 py-1.5 text-xs text-center focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleSubmit();
                    if (e.key === 'Escape') onCancel();
                  }}
                />
              </div>

              {/* IsGift */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-600">Brinde</label>
                <button
                  type="button"
                  onClick={() => setIsGift(v => !v)}
                  className={clsx(
                    'inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 font-medium transition-colors border',
                    isGift
                      ? 'bg-purple-100 text-purple-700 border-purple-300 hover:bg-purple-200'
                      : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50',
                  )}
                >
                  <Gift size={12} />
                  {isGift ? 'Sim – preço = R$ 0' : 'Não'}
                </button>
              </div>

              {/* UsePromoPrice */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-slate-600">Usar Preço Promocional</label>
                <button
                  type="button"
                  onClick={() => setUsePromoPrice(v => !v)}
                  disabled={isGift}
                  className={clsx(
                    'inline-flex items-center gap-1.5 text-xs rounded-full px-3 py-1.5 font-medium transition-colors border',
                    usePromoPrice && !isGift
                      ? 'bg-amber-100 text-amber-700 border-amber-300 hover:bg-amber-200'
                      : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50',
                    isGift && 'opacity-40 cursor-not-allowed',
                  )}
                >
                  <Percent size={12} />
                  {usePromoPrice && !isGift ? 'Sim' : 'Não'}
                </button>
              </div>

              {/* PromoPrice */}
              <div className="flex flex-col gap-1">
                <label className={clsx('text-xs font-medium', usePromoPrice && !isGift ? 'text-slate-600' : 'text-slate-400')}>
                  Preço Promocional
                </label>
                <div className="relative">
                  <span className={clsx('absolute left-2 top-1/2 -translate-y-1/2 text-xs', usePromoPrice && !isGift ? 'text-slate-500' : 'text-slate-300')}>
                    R$
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={promoPrice}
                    onChange={e => setPromoPrice(e.target.value)}
                    disabled={!usePromoPrice || isGift}
                    placeholder="0,00"
                    className={clsx(
                      'w-28 border rounded pl-7 pr-2 py-1.5 text-xs text-right focus:outline-none focus:ring-1 focus:ring-emerald-500',
                      usePromoPrice && !isGift
                        ? 'border-slate-300 bg-white'
                        : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed',
                    )}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSubmit();
                      if (e.key === 'Escape') onCancel();
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Linha 2: Ações + Erro */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!skuDetail.trim() || saving}
                className="inline-flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded px-4 py-1.5 font-medium disabled:opacity-50 transition-colors"
              >
                {saving ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
                {saving ? 'Adicionando...' : 'Adicionar'}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 transition-colors"
              >
                <X size={12} /> Cancelar
              </button>
              {displayError && (
                <span className="text-red-500 text-xs flex items-center gap-1">
                  <AlertCircle size={12} /> {displayError}
                </span>
              )}
            </div>
          </div>
        </td>
      </tr>
    </>
  );
};

// ─── Modal: confirmação de exclusão de detalhe ───────────────────────────────

interface DeleteDetailModalProps {
  skuMaster: string;
  skuDetail: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string;
}

const DeleteDetailModal: React.FC<DeleteDetailModalProps> = ({
  skuMaster,
  skuDetail,
  onConfirm,
  onCancel,
  loading,
  error,
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const confirmed = typed.trim().toUpperCase() === skuDetail.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <Trash2 size={22} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Remover produto filho</h2>
            <p className="text-sm text-slate-500 mt-1">
              Você está prestes a remover{' '}
              <span className="font-mono font-semibold text-slate-800">{skuDetail}</span>{' '}
              do composto{' '}
              <span className="font-mono font-semibold text-slate-800">{skuMaster}</span>.
              Esta ação não pode ser desfeita.
            </p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-red-700">
            Para confirmar, digite o <span className="font-semibold">SKU do produto filho</span>:
          </p>
          <p className="font-mono text-base font-bold text-red-800 tracking-widest bg-red-100 rounded-lg px-3 py-1.5">
            {skuDetail}
          </p>
          <input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value.toUpperCase())}
            placeholder={`Digite ${skuDetail}`}
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-red-400 bg-white placeholder-red-300"
            onKeyDown={e => {
              if (e.key === 'Enter' && confirmed) onConfirm();
              if (e.key === 'Escape') onCancel();
            }}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 border border-slate-300 text-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed || loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Removendo...</>
              : <><Trash2 size={14} /> Remover</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: confirmação de exclusão de master ─────────────────────────────────

interface DeleteMasterModalProps {
  skuMaster: string;
  description?: string | null;
  detailCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  error?: string;
}

const DeleteMasterModal: React.FC<DeleteMasterModalProps> = ({
  skuMaster,
  description,
  detailCount,
  onConfirm,
  onCancel,
  loading,
  error,
}) => {
  const [typed, setTyped] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const confirmed = typed.trim().toUpperCase() === skuMaster.toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertTriangle size={22} className="text-red-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Excluir produto composto</h2>
            <p className="text-sm text-slate-500 mt-1">
              Você está prestes a excluir permanentemente{' '}
              <span className="font-mono font-semibold text-slate-800">{skuMaster}</span>
              {description && <> &mdash; {description}</>}.
            </p>
            {detailCount > 0 && (
              <p className="text-sm text-amber-700 font-medium mt-1">
                ⚠️ {detailCount} produto{detailCount !== 1 ? 's' : ''} filho{detailCount !== 1 ? 's' : ''}{' '}
                também serão removidos. Esta ação não pode ser desfeita.
              </p>
            )}
            {detailCount === 0 && (
              <p className="text-sm text-red-600 mt-1">Esta ação não pode ser desfeita.</p>
            )}
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-red-700">
            Para confirmar a exclusão, digite o <span className="font-semibold">SKU do produto composto</span>:
          </p>
          <p className="font-mono text-base font-bold text-red-800 tracking-widest bg-red-100 rounded-lg px-3 py-1.5">
            {skuMaster}
          </p>
          <input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={e => setTyped(e.target.value.toUpperCase())}
            placeholder={`Digite ${skuMaster}`}
            className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-red-400 bg-white placeholder-red-300"
            onKeyDown={e => {
              if (e.key === 'Enter' && confirmed) onConfirm();
              if (e.key === 'Escape') onCancel();
            }}
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="flex-1 border border-slate-300 text-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!confirmed || loading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          >
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Excluindo...</>
              : <><Trash2 size={14} /> Excluir permanentemente</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── View Principal ───────────────────────────────────────────────────────────

export const CompositeProductListView: React.FC = () => {
  const navigate = useNavigate();

  const [masters, setMasters] = useState<MasterRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Campos de busca
  const [searchSkuMaster, setSearchSkuMaster] = useState('');
  const [searchSkuDetail, setSearchSkuDetail] = useState('');
  const [searchDescription, setSearchDescription] = useState('');

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 20;

  // Estados de adição de detalhe
  const [addDetailForSku, setAddDetailForSku] = useState<string | null>(null);
  const [addDetailSaving, setAddDetailSaving] = useState(false);
  const [addDetailError, setAddDetailError] = useState<string | undefined>(undefined);

  // Estados dos modais de exclusão
  const [deleteDetailModal, setDeleteDetailModal] = useState<{
    skuMaster: string;
    detailId: number;
    skuDetail: string;
  } | null>(null);
  const [deleteDetailError, setDeleteDetailError] = useState<string | undefined>(undefined);
  const [deleteDetailLoading, setDeleteDetailLoading] = useState(false);

  const [deleteMasterModal, setDeleteMasterModal] = useState<{
    skuMaster: string;
    description?: string | null;
    detailCount: number;
  } | null>(null);
  const [deleteMasterError, setDeleteMasterError] = useState<string | undefined>(undefined);
  const [deleteMasterLoading, setDeleteMasterLoading] = useState(false);

  // ── Carregamento de masters ─────────────────────────────────────────────────

  const loadMasters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await compositeProductService.getCompositeProducts({
        page: currentPage,
        pageSize,
        searchSkuMaster: searchSkuMaster.trim() || undefined,
        searchSkuDetail: searchSkuDetail.trim() || undefined,
        searchDescription: searchDescription.trim() || undefined,
      });

      setMasters(
        result.data.map(m => ({
          ...m,
          isExpanded: false,
          details: [],
          detailsLoaded: false,
          isEditingDescription: false,
          tempDescription: m.description ?? '',
          savingDescription: false,
          loadingDetails: false,
        })),
      );
      setTotalPages(result.pagination.totalPages);
      setTotalRecords(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar produtos compostos');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchSkuMaster, searchSkuDetail, searchDescription]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  // Debounce de busca
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchSkuMaster, searchSkuDetail, searchDescription]);

  // ── Toggle expand ───────────────────────────────────────────────────────────

  const toggleExpand = async (skuMaster: string) => {
    setMasters(prev =>
      prev.map(m => {
        if (m.skuMaster !== skuMaster) return m;
        const willExpand = !m.isExpanded;
        if (!willExpand) return { ...m, isExpanded: false };
        if (m.detailsLoaded) return { ...m, isExpanded: true };
        return { ...m, isExpanded: true, loadingDetails: true };
      }),
    );

    // Carregar detalhes se ainda não carregados
    const master = masters.find(m => m.skuMaster === skuMaster);
    if (!master?.detailsLoaded) {
      try {
        const details = await compositeProductService.getCompositeDetails(skuMaster);
        setMasters(prev =>
          prev.map(m =>
            m.skuMaster === skuMaster
              ? {
                  ...m,
                  details: details.map(toDetailRow),
                  detailsLoaded: true,
                  loadingDetails: false,
                }
              : m,
          ),
        );
      } catch (err) {
        setMasters(prev =>
          prev.map(m =>
            m.skuMaster === skuMaster ? { ...m, loadingDetails: false, isExpanded: false } : m,
          ),
        );
      }
    }
  };

  // ── Edição inline de Description (master) ──────────────────────────────────

  const startEditDescription = (skuMaster: string) => {
    setMasters(prev =>
      prev.map(m =>
        m.skuMaster === skuMaster
          ? { ...m, isEditingDescription: true, tempDescription: m.description ?? '' }
          : m,
      ),
    );
  };

  const cancelEditDescription = (skuMaster: string) => {
    setMasters(prev =>
      prev.map(m =>
        m.skuMaster === skuMaster ? { ...m, isEditingDescription: false } : m,
      ),
    );
  };

  const saveDescription = async (skuMaster: string) => {
    const master = masters.find(m => m.skuMaster === skuMaster);
    if (!master) return;

    setMasters(prev =>
      prev.map(m => (m.skuMaster === skuMaster ? { ...m, savingDescription: true } : m)),
    );

    try {
      const updated = await compositeProductService.updateCompositeProduct(
        skuMaster,
        master.tempDescription,
      );
      setMasters(prev =>
        prev.map(m =>
          m.skuMaster === skuMaster
            ? { ...m, description: updated.description, isEditingDescription: false, savingDescription: false }
            : m,
        ),
      );
    } catch (err) {
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, savingDescription: false } : m)),
      );
    }
  };

  // ── Edição inline de detalhe ────────────────────────────────────────────────

  const updateDetail = (
    skuMaster: string,
    detailId: number,
    fields: Partial<DetailRow>,
  ) => {
    setMasters(prev =>
      prev.map(m =>
        m.skuMaster !== skuMaster
          ? m
          : {
              ...m,
              details: m.details.map(d => (d.id === detailId ? { ...d, ...fields } : d)),
            },
      ),
    );
  };

  const cancelEditDetail = (skuMaster: string, detailId: number) => {
    setMasters(prev =>
      prev.map(m =>
        m.skuMaster !== skuMaster
          ? m
          : {
              ...m,
              details: m.details.map(d =>
                d.id === detailId
                  ? {
                      ...d,
                      isEditing: false,
                      tempQty: String(d.skuDetailQty),
                      tempPromoPrice: d.promoPrice != null ? String(d.promoPrice) : '',
                      hasError: false,
                    }
                  : d,
              ),
            },
      ),
    );
  };

  const saveDetail = async (skuMaster: string, detailId: number) => {
    const master = masters.find(m => m.skuMaster === skuMaster);
    const detail = master?.details.find(d => d.id === detailId);
    if (!detail) return;

    const qty = parseInt(detail.tempQty, 10);
    if (isNaN(qty) || qty < 1) {
      updateDetail(skuMaster, detailId, {
        hasError: true,
        errorMessage: 'Quantidade deve ser maior que zero',
      });
      return;
    }

    const promoPrice =
      detail.usePromoPrice && detail.tempPromoPrice
        ? parseFloat(detail.tempPromoPrice)
        : detail.promoPrice;

    if (detail.usePromoPrice && (promoPrice == null || isNaN(promoPrice) || promoPrice < 0)) {
      updateDetail(skuMaster, detailId, {
        hasError: true,
        errorMessage: 'Preço promocional inválido',
      });
      return;
    }

    updateDetail(skuMaster, detailId, { isSaving: true, hasError: false });

    const updateData: ProductAggregationDetailUpdateData = {
      skuDetailQty: qty,
      isGift: detail.isGift,
      promoPrice: detail.usePromoPrice ? promoPrice : null,
      usePromoPrice: detail.usePromoPrice ?? false,
    };

    try {
      const updated = await compositeProductService.updateCompositeDetail(
        skuMaster,
        detailId,
        updateData,
      );
      setMasters(prev =>
        prev.map(m =>
          m.skuMaster !== skuMaster
            ? m
            : {
                ...m,
                details: m.details.map(d =>
                  d.id === detailId
                    ? {
                        ...toDetailRow(updated),
                        productDescription: d.productDescription,
                        originalPrice: d.originalPrice,
                      }
                    : d,
                ),
              },
        ),
      );
      refreshMasterPrice(skuMaster);
    } catch (err) {
      updateDetail(skuMaster, detailId, {
        isSaving: false,
        hasError: true,
        errorMessage: err instanceof Error ? err.message : 'Erro ao salvar',
      });
    }
  };

  // ── Exclusão de detalhe ─────────────────────────────────────────────────────

  const deleteDetail = (skuMaster: string, detailId: number, skuDetail: string) => {
    setDeleteDetailError(undefined);
    setDeleteDetailModal({ skuMaster, detailId, skuDetail });
  };

  const confirmDeleteDetail = async () => {
    if (!deleteDetailModal) return;
    setDeleteDetailLoading(true);
    setDeleteDetailError(undefined);
    try {
      await compositeProductService.removeCompositeDetail(
        deleteDetailModal.skuMaster,
        deleteDetailModal.detailId,
      );
      setMasters(prev =>
        prev.map(m =>
          m.skuMaster !== deleteDetailModal.skuMaster
            ? m
            : { ...m, details: m.details.filter(d => d.id !== deleteDetailModal.detailId) },
        ),
      );
      refreshMasterPrice(deleteDetailModal.skuMaster);
      setDeleteDetailModal(null);
    } catch (err) {
      setDeleteDetailError(err instanceof Error ? err.message : 'Erro ao remover produto filho');
    } finally {
      setDeleteDetailLoading(false);
    }
  };

  // ── Adição de detalhe ───────────────────────────────────────────────────────

  const handleAddDetail = async (
    skuMaster: string,
    skuDetail: string,
    qty: number,
    isGift: boolean,
    promoPrice: number | null,
    usePromoPrice: boolean,
  ) => {
    setAddDetailSaving(true);
    setAddDetailError(undefined);
    try {
      const newDetail = await compositeProductService.addCompositeDetail(skuMaster, {
        skuDetail,
        skuDetailQty: qty,
        isGift,
        promoPrice,
        usePromoPrice,
      });
      setMasters(prev =>
        prev.map(m =>
          m.skuMaster !== skuMaster
            ? m
            : { ...m, details: [...m.details, toDetailRow(newDetail)] },
        ),
      );
      refreshMasterPrice(skuMaster);
      setAddDetailForSku(null);
    } catch (err) {
      setAddDetailError(err instanceof Error ? err.message : 'Erro ao adicionar produto filho');
    } finally {
      setAddDetailSaving(false);
    }
  };

  // ── Exclusão de master ──────────────────────────────────────────────────────

  const deleteMaster = (skuMaster: string) => {
    const master = masters.find(m => m.skuMaster === skuMaster);
    setDeleteMasterError(undefined);
    setDeleteMasterModal({
      skuMaster,
      description: master?.description,
      detailCount: master?.detailsLoaded ? master.details.length : 0,
    });
  };

  const confirmDeleteMaster = async () => {
    if (!deleteMasterModal) return;
    setDeleteMasterLoading(true);
    setDeleteMasterError(undefined);
    try {
      await compositeProductService.deleteCompositeProduct(deleteMasterModal.skuMaster);
      setMasters(prev => prev.filter(m => m.skuMaster !== deleteMasterModal.skuMaster));
      setTotalRecords(prev => prev - 1);
      setDeleteMasterModal(null);
    } catch (err) {
      setDeleteMasterError(err instanceof Error ? err.message : 'Erro ao excluir produto composto');
    } finally {
      setDeleteMasterLoading(false);
    }
  };

  // ── Refresh master price ────────────────────────────────────────────────────

  const refreshMasterPrice = useCallback(async (skuMaster: string) => {
    try {
      const refreshed = await compositeProductService.getCompositeProductBySkuMaster(skuMaster);
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, price: refreshed.price } : m)),
      );
    } catch {
      // silently fail – the price column will show stale value
    }
  }, []);

  // ── Toggle isActive ─────────────────────────────────────────────────────────

  const handleToggleActive = useCallback(async (skuMaster: string, newValue: boolean) => {
    // Optimistic update
    setMasters(prev =>
      prev.map(m => (m.skuMaster === skuMaster ? { ...m, isActive: newValue } : m)),
    );
    try {
      const updated = await compositeProductService.toggleCompositeProductActive(skuMaster, newValue);
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, isActive: updated.isActive } : m)),
      );
    } catch {
      // Revert on failure
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, isActive: !newValue } : m)),
      );
    }
  }, []);

  // ── Toggle isVisibleOnCRM ───────────────────────────────────────────────────

  const handleToggleVisibleOnCRM = useCallback(async (skuMaster: string, newValue: boolean) => {
    // Optimistic update
    setMasters(prev =>
      prev.map(m => (m.skuMaster === skuMaster ? { ...m, isVisibleOnCRM: newValue } : m)),
    );
    try {
      const updated = await compositeProductService.toggleCompositeProductVisibleOnCRM(skuMaster, newValue);
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, isVisibleOnCRM: updated.isVisibleOnCRM } : m)),
      );
    } catch {
      // Revert on failure
      setMasters(prev =>
        prev.map(m => (m.skuMaster === skuMaster ? { ...m, isVisibleOnCRM: !newValue } : m)),
      );
    }
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Modais de exclusão */}
      {deleteDetailModal && (
        <DeleteDetailModal
          skuMaster={deleteDetailModal.skuMaster}
          skuDetail={deleteDetailModal.skuDetail}
          onConfirm={confirmDeleteDetail}
          onCancel={() => { setDeleteDetailModal(null); setDeleteDetailError(undefined); }}
          loading={deleteDetailLoading}
          error={deleteDetailError}
        />
      )}
      {deleteMasterModal && (
        <DeleteMasterModal
          skuMaster={deleteMasterModal.skuMaster}
          description={deleteMasterModal.description}
          detailCount={deleteMasterModal.detailCount}
          onConfirm={confirmDeleteMaster}
          onCancel={() => { setDeleteMasterModal(null); setDeleteMasterError(undefined); }}
          loading={deleteMasterLoading}
          error={deleteMasterError}
        />
      )}

      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Produtos Compostos</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Gerencie kits e bundles que agrupam múltiplos produtos
          </p>
        </div>
        <PermissionGuard permission="composite_products.create">
          <Button
            variant="primary"
            onClick={() => navigate('/composite-products/new')}
            className="flex items-center gap-2"
          >
            <Plus size={16} />
            Novo Produto Composto
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtros de busca */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por SKU Master..."
                value={searchSkuMaster}
                onChange={e => setSearchSkuMaster(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="relative">
              <Tag size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por SKU Filho..."
                value={searchSkuDetail}
                onChange={e => setSearchSkuDetail(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar por descrição..."
                value={searchDescription}
                onChange={e => setSearchDescription(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback de erro global */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <AlertCircle size={18} />
          <span className="text-sm">{error}</span>
          <button
            className="ml-auto text-red-500 hover:text-red-700"
            onClick={loadMasters}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      )}

      {/* Tabela */}
      <Card>
        <CardHeader
          title="Produtos Compostos"
          action={
            <span className="text-sm text-slate-500">
              {loading ? 'Carregando...' : `${totalRecords} produto(s) composto(s)`}
            </span>
          }
        />
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-slate-400" />
            </div>
          ) : masters.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Package size={40} className="mx-auto mb-3 text-slate-300" />
              <p className="font-medium">Nenhum produto composto encontrado</p>
              <PermissionGuard permission="composite_products.create">
                <button
                  className="mt-3 text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                  onClick={() => navigate('/composite-products/new')}
                >
                  Criar o primeiro produto composto
                </button>
              </PermissionGuard>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-10" />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      SKU Master
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Descrição
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Total Calculado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Ativo
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      CRM
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Itens Filhos
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {masters.map(master => {
                    const total = calcCompositeTotal(master.details);
                    const detailCount = master.detailsLoaded ? master.details.length : '—';

                    return (
                      <React.Fragment key={master.skuMaster}>
                        {/* ── Linha Master ──────────────────────────────────── */}
                        <tr
                          className={clsx(
                            'border-b border-slate-200 transition-colors',
                            master.isExpanded ? 'bg-emerald-50' : 'bg-white hover:bg-slate-50',
                          )}
                        >
                          {/* Toggle */}
                          <td className="px-4 py-3 w-10">
                            <button
                              type="button"
                              onClick={() => toggleExpand(master.skuMaster)}
                              className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-500"
                              title={master.isExpanded ? 'Recolher produtos filhos' : 'Expandir produtos filhos'}
                            >
                              {master.loadingDetails ? (
                                <RefreshCw size={16} className="animate-spin" />
                              ) : master.isExpanded ? (
                                <ChevronDown size={16} />
                              ) : (
                                <ChevronRight size={16} />
                              )}
                            </button>
                          </td>

                          {/* SKU Master */}
                          <td className="px-4 py-3 font-mono font-semibold text-slate-800">
                            {master.skuMaster}
                          </td>

                          {/* Descrição – inline editable */}
                          <td className="px-4 py-3 max-w-xs">
                            {master.isEditingDescription ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={master.tempDescription}
                                  onChange={e =>
                                    setMasters(prev =>
                                      prev.map(m =>
                                        m.skuMaster === master.skuMaster
                                          ? { ...m, tempDescription: e.target.value }
                                          : m,
                                      ),
                                    )
                                  }
                                  className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                  maxLength={150}
                                  autoFocus
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveDescription(master.skuMaster);
                                    if (e.key === 'Escape') cancelEditDescription(master.skuMaster);
                                  }}
                                />
                                <button
                                  onClick={() => saveDescription(master.skuMaster)}
                                  disabled={master.savingDescription}
                                  className="p-1 text-emerald-600 hover:text-emerald-700 disabled:opacity-50"
                                >
                                  {master.savingDescription ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                  ) : (
                                    <Save size={16} />
                                  )}
                                </button>
                                <button
                                  onClick={() => cancelEditDescription(master.skuMaster)}
                                  className="p-1 text-slate-400 hover:text-slate-600"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <PermissionGuard
                                permission="composite_products.write"
                                fallback={
                                  <span className="text-slate-700">
                                    {master.description ?? <span className="text-slate-400 italic">sem descrição</span>}
                                  </span>
                                }
                              >
                                <button
                                  type="button"
                                  onClick={() => startEditDescription(master.skuMaster)}
                                  className="text-left text-slate-700 hover:text-emerald-700 group flex items-center gap-1 w-full"
                                  title="Clique para editar a descrição"
                                >
                                  <span className="truncate">
                                    {master.description ?? (
                                      <span className="text-slate-400 italic">sem descrição</span>
                                    )}
                                  </span>
                                  <Edit2 size={13} className="opacity-0 group-hover:opacity-50 flex-shrink-0" />
                                </button>
                              </PermissionGuard>
                            )}
                          </td>

                          {/* Total */}
                          <td className="px-4 py-3 text-right font-bold text-emerald-700 whitespace-nowrap">
                            {master.price != null ? formatCurrency(Math.floor(master.price)) : '—'}
                          </td>

                          {/* IsActive toggle */}
                          <td className="px-4 py-3 text-center">
                            <PermissionGuard
                              permission="composite_products.write"
                              fallback={
                                <span className={clsx(
                                  'inline-flex text-xs rounded-full px-2 py-0.5 font-medium',
                                  (master.isActive ?? true)
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-500',
                                )}>
                                  {(master.isActive ?? true) ? 'Ativo' : 'Inativo'}
                                </span>
                              }
                            >
                              <button
                                type="button"
                                onClick={() => handleToggleActive(master.skuMaster, !(master.isActive ?? true))}
                                className={clsx(
                                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1',
                                  (master.isActive ?? true) ? 'bg-emerald-500' : 'bg-slate-300',
                                )}
                                title={(master.isActive ?? true) ? 'Desativar' : 'Ativar'}
                                role="switch"
                                aria-checked={(master.isActive ?? true)}
                              >
                                <span
                                  className={clsx(
                                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200',
                                    (master.isActive ?? true) ? 'translate-x-4' : 'translate-x-0',
                                  )}
                                />
                              </button>
                            </PermissionGuard>
                          </td>

                          {/* IsVisibleOnCRM toggle */}
                          <td className="px-4 py-3 text-center">
                            <PermissionGuard
                              permission="composite_products.write"
                              fallback={
                                <span className={clsx(
                                  'inline-flex text-xs rounded-full px-2 py-0.5 font-medium',
                                  (master.isVisibleOnCRM ?? true)
                                    ? 'bg-blue-100 text-blue-700'
                                    : 'bg-slate-100 text-slate-500',
                                )}>
                                  {(master.isVisibleOnCRM ?? true) ? 'Sim' : 'Não'}
                                </span>
                              }
                            >
                              <button
                                type="button"
                                onClick={() => handleToggleVisibleOnCRM(master.skuMaster, !(master.isVisibleOnCRM ?? true))}
                                className={clsx(
                                  'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
                                  (master.isVisibleOnCRM ?? true) ? 'bg-blue-500' : 'bg-slate-300',
                                )}
                                title={(master.isVisibleOnCRM ?? true) ? 'Ocultar do CRM' : 'Exibir no CRM'}
                                role="switch"
                                aria-checked={(master.isVisibleOnCRM ?? true)}
                              >
                                <span
                                  className={clsx(
                                    'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200',
                                    (master.isVisibleOnCRM ?? true) ? 'translate-x-4' : 'translate-x-0',
                                  )}
                                />
                              </button>
                            </PermissionGuard>
                          </td>

                          {/* Nº de itens */}
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold">
                              {detailCount}
                            </span>
                          </td>

                          {/* Ações master */}
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <PermissionGuard permission="composite_products.delete">
                                <button
                                  type="button"
                                  onClick={() => deleteMaster(master.skuMaster)}
                                  disabled={deleteMasterLoading && deleteMasterModal?.skuMaster === master.skuMaster}
                                  className="p-1.5 text-slate-400 hover:text-red-500 disabled:opacity-50 transition-colors"
                                  title="Excluir produto composto"
                                >
                                  {deleteMasterLoading && deleteMasterModal?.skuMaster === master.skuMaster ? (
                                    <RefreshCw size={16} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={16} />
                                  )}
                                </button>
                              </PermissionGuard>
                            </div>
                          </td>
                        </tr>

                        {/* ── Linhas de Detalhe (expandidas) ───────────────── */}
                        {master.isExpanded && (
                          <>
                            {/* Cabeçalho de detalhes — 11 colunas espelhando DetailRowItem */}
                            <tr className="bg-slate-100 border-b border-slate-200">
                              {/* indent — alinha com o toggle da master row */}
                              <td className="w-10" />
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                SKU Filho
                              </th>
                              <th className="px-4 py-1.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Descrição do Produto
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Preço Original
                              </th>
                              <th className="px-4 py-1.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Qtd
                              </th>
                              <th className="px-4 py-1.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                Brinde
                              </th>
                              <th className="px-4 py-1.5 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Usa Promo
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Preço Promo
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Preço Efetivo
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Total Linha
                              </th>
                              <th className="px-4 py-1.5 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                                Ações
                              </th>
                            </tr>

                            {master.details.length === 0 && !master.loadingDetails ? (
                              <tr className="bg-slate-50 border-b border-slate-100">
                                <td colSpan={11} className="px-4 py-4 text-center text-slate-400 text-sm italic">
                                  Nenhum produto filho cadastrado
                                </td>
                              </tr>
                            ) : (
                              master.details.map(detail => (
                                <DetailRowItem
                                  key={detail.id}
                                  row={detail}
                                  canWrite={true} // PermissionGuard irá bloquear se necessário
                                  onUpdate={fields => updateDetail(master.skuMaster, detail.id, fields)}
                                  onSave={() => saveDetail(master.skuMaster, detail.id)}
                                  onCancel={() => cancelEditDetail(master.skuMaster, detail.id)}
                                  onDelete={() => deleteDetail(master.skuMaster, detail.id, detail.skuDetail)}
                                />
                              ))
                            )}

                            {/* Formulário de adição de detalhe */}
                            {addDetailForSku === master.skuMaster && (
                              <AddDetailForm
                                skuMaster={master.skuMaster}
                                onAdd={(skuDetail, qty, isGift, promoPrice, usePromoPrice) =>
                                  handleAddDetail(master.skuMaster, skuDetail, qty, isGift, promoPrice, usePromoPrice)
                                }
                                onCancel={() => {
                                  setAddDetailForSku(null);
                                  setAddDetailError(undefined);
                                }}
                                saving={addDetailSaving}
                                error={addDetailError}
                              />
                            )}

                            {/* Rodapé de detalhes: add filho + total */}
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <td colSpan={4} className="px-4 py-2">
                                <PermissionGuard permission="composite_products.write">
                                  {addDetailForSku !== master.skuMaster && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAddDetailForSku(master.skuMaster);
                                        setAddDetailError(undefined);
                                      }}
                                      className="inline-flex items-center gap-1.5 text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                    >
                                      <Plus size={14} />
                                      Adicionar produto filho
                                    </button>
                                  )}
                                </PermissionGuard>
                              </td>
                              <td colSpan={5} />
                              <td className="px-4 py-2 text-right text-xs font-semibold text-slate-600 whitespace-nowrap">
                                Total:
                              </td>
                              <td className="px-4 py-2 text-right font-bold text-emerald-700 text-sm whitespace-nowrap">
                                {formatCurrency(total)}
                              </td>
                            </tr>
                          </>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Página {currentPage} de {totalPages} — {totalRecords} registro(s)
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
            >
              Anterior
            </Button>
            <Button
              variant="secondary"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
